import type { DataExportScheduleExecutionResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetDataExportScheduleExecutionsSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetDataExportScheduleExecutionsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

type UseGetDataExportScheduleExecutionsArgs = Record<string, never>;

/**
 * Poll interval for the export-history feed (COI-772).
 *
 * 5s detail-feed bracket: advanced by the export executor, not the user. Fixed
 * rather than data-driven like `useGetDataImportStatusSubscription` — `scheduled`
 * rows sit indefinitely pending the next cron fire (see
 * `DataExportExecutionStatus.Cancelled`'s doc for why), so a stop-when-terminal
 * rule would never fire, and the feed is open-ended (new rows keep arriving), so
 * it must never return `false`.
 */
const POLL_INTERVAL_MS = 5000;

export const useGetDataExportScheduleExecutions = createSubscriptionHook<
  UseGetDataExportScheduleExecutionsArgs,
  DataExportScheduleExecutionResponseRow[],
  GetDataExportScheduleExecutionsSubscription
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.dataExport.getScheduleExecutions.queryOptions(),
  trpcRefetchInterval: () => POLL_INTERVAL_MS,
  mapTrpcDataToGraphQL: (data) => ({
    data_export_schedule_execution: data,
  }),
  graphqlDocument: GetDataExportScheduleExecutionsDocument,
});
